import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Colorful } from "@uiw/react-color";

import { usePatterns } from "../store/PatternContext";
import { backPage, selectCurrentPattern, setCurrentPattern, setPage } from "../store/patternManagerSlice";
import { dummyPattern, isLightningPattern, isLightningPatternKey, isSamePattern, isTwistedStripePattern, isTwistedStripePatternKey, keysForPattern, LightningPattern, Pattern, PatternKey, PatternType, StoredPattern, TwistedStripePattern } from "../types/pattern.model";
import { Color, ColorScheme, Hex } from "../types/color.model";
import { generateLightningPattern, generateTwistedStripePattern } from "../utils/pattern.utils";

import LightningPatternComponent from "../patterns/LightningPattern";
import TwistedStripePatternComponent from "../patterns/TwistedStripePattern";
import Button from "./Button";
import ColorSchemeDropdown from "./ColorSchemeDropdown";
import CollectionDropdown from "./CollectionDropdown";

import ArchiveIcon from "../icons/ArchiveIcon";
import CheckIcon from "../icons/CheckIcon";
import LeftArrow from "../icons/LeftArrow";
import TrashIcon from "../icons/TrashIcon";
import ShuffleIcon from "../icons/ShuffleIcon";
import DoubleArrowIcon from "../icons/DoubleArrowIcon";
import PencilIcon from "../icons/PencilIcon";
import { overlayIconColor } from "../utils/color.utils";

const PatternPreview = ({pattern, type}: {pattern: Pattern, type: PatternType}) => {
  switch (type) {
    case 'lightning':
      const {outside, middle, inside} = pattern as LightningPattern;
      return <LightningPatternComponent outside={outside} middle={middle} inside={inside} />;
    case 'twisted-stripe':
      const {stripe1, stripe2, stripe3, stripe4, stripe5} = pattern as TwistedStripePattern;
      return <TwistedStripePatternComponent stripe1={stripe1} stripe2={stripe2} stripe3={stripe3} stripe4={stripe4} stripe5={stripe5} />;
  }
};

const patternColor = (pattern: Pattern, key: PatternKey): Color | null => {
  if (isLightningPattern(pattern) && isLightningPatternKey(key)) {
    return pattern[key];
  } else if (isTwistedStripePattern(pattern) && isTwistedStripePatternKey(key)) {
    return pattern[key];
  } else {
    return null;
  }
};

const updatePatternColor = (pattern: Pattern, key: PatternKey, color: Color): Pattern => {
  if (isLightningPattern(pattern) && isLightningPatternKey(key)) {
    return {...pattern, [key]: color};
  } else if (isTwistedStripePattern(pattern) && isTwistedStripePatternKey(key)) {
    return {...pattern, [key]: color};
  } else {
    return pattern;
  }
};

interface ColorRowProps {
  patternKey: PatternKey;
  color: Color;
  rearranging: boolean;
  editingKey: PatternKey | null;
  onClickKey: (key: PatternKey) => void;
  onMoveUp?: (key: PatternKey) => void;
  onMoveDown?: (key: PatternKey) => void;
}

const ColorRow: React.FC<ColorRowProps> = ({
  patternKey,
  color,
  rearranging,
  editingKey,
  onClickKey,
  onMoveUp,
  onMoveDown
}) => {
  const overlayColor = overlayIconColor(color);

  if (rearranging) {
    // display color block + up/down buttons
    return (
      <div className="flex items-center gap-1 h-8" key={`color-${patternKey}`}>
        <p className="w-14 text-sm">{patternKey}:</p>
        <div className="flex flex-1 justify-around items-center h-8 cursor-pointer rounded" style={{backgroundColor: color}}>
          {onMoveUp && <div onClick={() => onMoveUp(patternKey)} className="flex items-center cursor-pointer" style={{color: overlayColor}}>↑</div>}
          {onMoveDown && <div onClick={() => onMoveDown(patternKey)} className="flex items-center cursor-pointer"style={{color: overlayColor}}>↓</div>}
        </div>
      </div>
    )
  } else {
    // display color text + edit/save button
    return (
      <div className="flex items-baseline gap-1 h-8" key={`color-${patternKey}`}>
        <p className="w-14 text-sm">{patternKey}:</p>
        <div className="flex flex-1 justify-around items-center h-8 cursor-pointer rounded" style={{backgroundColor: color}} onClick={() => onClickKey(patternKey)}>
          <p className="w-16 text-sm" style={{color: overlayColor}}>{String(color).toUpperCase()}</p>
          <div style={{color: overlayColor}}>{editingKey === patternKey ? <CheckIcon /> : <PencilIcon />}</div>
        </div>
      </div>
    )
  }
};

interface BraceletMakerProps {
  patternType: PatternType;
  isNewPattern?: boolean;
}

const BraceletMaker: React.FC<BraceletMakerProps> = ({ patternType, isNewPattern }) => {
  const originalPattern = useSelector(selectCurrentPattern);

  const { addPattern, updatePattern, deletePattern, archivePattern, unarchivePattern } = usePatterns(patternType);

  const [pattern, setPattern] = useState<Pattern>(originalPattern || dummyPattern(patternType));
  const [name, setName] = useState<string>(originalPattern?.name ?? "");
  const [collectionId, setCollectionId] = useState<string | null>(originalPattern?.collectionId ?? null);

  const patternKeys = keysForPattern(patternType);

  const [editingKey, setEditingKey] = useState<PatternKey | null>(null);
  const [editingColor, setEditingColor] = useState<Color | null>(null);

  const [selectedScheme, setSelectedScheme] = useState<ColorScheme>("random");

  const [rearranging, setRearranging] = useState(false);

  const [ saveStatus, setSaveStatus ] = useState<"normal" | "loading" | "success">("normal");
  const [ error, setError ] = useState<string | null>(null);

  const dispatch = useDispatch();

  const back = () => {
    dispatch(setCurrentPattern(null));
    dispatch(backPage());
  };

  const onClickKey = (key: PatternKey) => {
    if (editingKey === key) {
      setEditingKey(null);
      setEditingColor(null);
    } else {
      setEditingKey(key);
      setEditingColor(patternColor(pattern, key));
    }
  };

  const onEditColor = (key: PatternKey, color: Color) => {
    setEditingColor(color);
    setPatternColor(key, color);
  };

  const setPatternColor = (key: PatternKey, color: Color) => {
    const newPattern = updatePatternColor(pattern, key, color);
    setPattern(newPattern);
  };

  const savePattern = async () => {
    const trimmedName = name.trim();
    if (
      originalPattern &&
      isSamePattern(originalPattern, pattern, patternType) &&
      (originalPattern.name ?? "") === trimmedName &&
      originalPattern.collectionId === collectionId
    ) {
      return;
    }
    setError(null);
    setSaveStatus("loading");
    try {
      const saveInput = { name: trimmedName === "" ? null : trimmedName, collectionId };
      if (!isNewPattern && originalPattern) {
        await updatePattern(originalPattern.id, pattern, saveInput);
      } else {
        await addPattern(pattern, saveInput);
      }
      setSaveStatus("success");
      setTimeout(() => {
        dispatch(setCurrentPattern(null));
        dispatch(setPage('home'));
      }, 500);
    } catch (err) {
      setSaveStatus("normal");
      setError(err instanceof Error ? err.message : "Failed to save pattern.");
    }
  };

  const confirmDelete = async (pattern: StoredPattern) => {
    if (!window.confirm("Delete this pattern?")) return;

    setError(null);
    try {
      await deletePattern(pattern.id);
      dispatch(setCurrentPattern(null));
      dispatch(setPage('home'));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete pattern.");
    }
  };

  const toggleArchived = async (pattern: StoredPattern) => {
    setError(null);
    try {
      if (pattern.archivedAt) {
        await unarchivePattern(pattern.id);
      } else {
        await archivePattern(pattern.id);
      }
      dispatch(setCurrentPattern(null));
      dispatch(setPage('home'));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update pattern.");
    }
  };

  const generatePattern = () => {
    let newPattern;

    if (patternType === 'lightning') {
      newPattern = generateLightningPattern(selectedScheme);
    } else if (patternType === 'twisted-stripe') {
      newPattern = generateTwistedStripePattern(selectedScheme);
    }

    if (newPattern) {
      setPattern(newPattern);
    }
  };

  const moveUp = (currentKey: PatternKey) => {
    const currentIdx = patternKeys.indexOf(currentKey);
    if (currentIdx <= 0) return;

    let newPattern = {...pattern};
    const currentColor = patternColor(pattern, currentKey);
    const aboveKey = patternKeys[currentIdx - 1];
    const aboveColor = patternColor(pattern, aboveKey);

    // swap colors
    if (currentColor && aboveColor) {
      newPattern = updatePatternColor(newPattern, currentKey, aboveColor);
      newPattern = updatePatternColor(newPattern, aboveKey, currentColor);
      setPattern(newPattern);
    }
  };

  const moveDown = (currentKey: PatternKey) => {
    const currentIdx = patternKeys.indexOf(currentKey);
    if (currentIdx === -1 || currentIdx >= patternKeys.length - 1) return;

    let newPattern = {...pattern};
    const currentColor = patternColor(pattern, currentKey);
    const belowKey = patternKeys[currentIdx + 1];
    const belowColor = patternColor(pattern, belowKey);

    // swap colors
    if (currentColor && belowColor) {
      newPattern = updatePatternColor(newPattern, currentKey, belowColor);
      newPattern = updatePatternColor(newPattern, belowKey, currentColor);
      setPattern(newPattern);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-center w-full gap-2 my-4 relative">
        <div className="flex items-center cursor-pointer absolute left-4" onClick={back}><LeftArrow /></div>
        <p className="text-lg">{isNewPattern ? "Create a New Pattern" : "Edit Your Pattern"}</p>
      </div>
      <input
        type="text"
        className="border border-gray-500 rounded-md px-2 py-1 text-sm mb-4 w-48 text-center"
        placeholder="Name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={saveStatus === "loading"}
      />
      <div className="w-48 mb-4">
        <CollectionDropdown
          selectedCollectionId={collectionId}
          onSelect={setCollectionId}
          disabled={saveStatus === "loading"}
        />
      </div>
      <div className="flex w-full justify-center">
        <div id="bracelet-preview" className="mr-4">
          <PatternPreview pattern={pattern} type={patternType} />
        </div>
        <div id="color-editor" className="flex flex-col min-w-[200px]">
          <div className="flex flex-col gap-4 mb-4">
            {patternKeys.map((key, idx) => {
              const color = patternColor(pattern, key);
              if (!color) { return null; }

              return (
                <ColorRow 
                  patternKey={key} 
                  color={color} 
                  rearranging={rearranging} 
                  editingKey={editingKey} 
                  onClickKey={onClickKey} 
                  onMoveUp={idx > 0 ? moveUp : undefined}
                  onMoveDown={idx < patternKeys.length - 1 ? moveDown : undefined}
                />
              )
            })}
          </div>
          {
            editingKey && editingColor &&
            <Colorful color={editingColor} onChange={(color) => onEditColor(editingKey, color.hex as Hex)} />
          }
        </div>
      </div>
      <div id="button-container" className="flex flex-col items-center my-4 gap-4">
        <ColorSchemeDropdown selectedScheme={selectedScheme} onSchemeSelect={setSelectedScheme} disabled={saveStatus === "loading"} />
        <Button onClick={generatePattern} disabled={saveStatus === "loading"} Icon={<ShuffleIcon />}>
          <p className="text-sm">Generate Colors</p>
        </Button>
        <Button onClick={() => setRearranging(!rearranging)} disabled={saveStatus === "loading"} Icon={<DoubleArrowIcon />}>
          <p className="text-sm">{rearranging ? "Stop Rearranging" : "Rearrange Colors"}</p>
        </Button>
        <div className="flex items-center gap-4 flex-wrap">
          <Button onClick={savePattern} disabled={saveStatus === "loading"} Icon={<CheckIcon />}>
            <p className="text-sm">{saveStatus === "loading" ? "Saving..." : "Save Pattern"}</p>
          </Button>
          {
            !isNewPattern && originalPattern && (
              <Button onClick={() => toggleArchived(originalPattern)} Icon={<ArchiveIcon />}>
                <p className="text-sm">{originalPattern.archivedAt ? "Unarchive Pattern" : "Archive Pattern"}</p>
              </Button>
            )
          }
          {
            !isNewPattern && originalPattern && (
              <Button onClick={() => confirmDelete(originalPattern)} Icon={<TrashIcon />}>
                <p className="text-sm">Delete Pattern</p>
              </Button>
            )
          }
        </div>
      </div>
      {saveStatus === "success" && <p className="">Successfully saved pattern!</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  )
};

export default BraceletMaker;
